import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Icons from '../Icons';
import { mapChildrenTypes, getDisplayName } from './page-utils';
import {
  PageHeader,
  PageTopPanel,
  PageContent,
  PageContentHeader,
  PageContentLeft,
  PageContentRight,
  PageContentBottom
} from './PageComponents';
import * as S from './PageStyled';

const isVisible = comp => {
  if (typeof comp?.props?.visible === 'boolean') return comp?.props?.visible;
  return !!comp;
};

export const Page = ({ children, className }) => {
  const childMap = mapChildrenTypes(children);
  const header = childMap[getDisplayName(PageHeader)];
  const topPanel = childMap[getDisplayName(PageTopPanel)];
  const contentHeader = childMap[getDisplayName(PageContentHeader)];
  const content = childMap[getDisplayName(PageContent)];
  const left = childMap[getDisplayName(PageContentLeft)];
  const right = childMap[getDisplayName(PageContentRight)];
  const bottom = childMap[getDisplayName(PageContentBottom)];

  /** If left is floating it need to be rendered after content DOM to have higher order
   * else (pushes the content), it needs to be before content DOM element. */
  const leftFloating = left?.props?.floating;
  const rightFloating = right?.props?.floating;

  const topPanelVisible = isVisible(topPanel);

  const topPanelCollapsible = !!topPanel?.props?.collapsible;
  const [topPanelCollapsed, setTopPanelCollapsed] = useState(false);
  const topPanelExpanded = topPanelVisible && !topPanelCollapsed;

  return (
    <S.Container className={className}>
      {header}
      <S.TopPanelArea>
        <S.TopAnimated $visible={topPanelExpanded}>{topPanel}</S.TopAnimated>
        {topPanelCollapsible && (
          <S.TopPanelToggle
            $expanded={topPanelExpanded}
            aria-expanded={topPanelExpanded}
            aria-label="Toggle top panel"
            data-testid={topPanel?.props?.toggleTestId || 'page-top-panel-toggle'}
            onClick={() => setTopPanelCollapsed(collapsed => !collapsed)}>
            <Icons.Collapse className="collapse-icon" />
          </S.TopPanelToggle>
        )}
      </S.TopPanelArea>
      <S.MainArea>
        {contentHeader}
        <S.MainAreaContentWrapper>
          {!leftFloating && (
            <S.LeftAnimated $visible={isVisible(left)} $floating={leftFloating}>
              {left}
            </S.LeftAnimated>
          )}
          {content}
          {leftFloating && (
            <S.LeftAnimated $visible={isVisible(left)} $floating={leftFloating}>
              {left}
            </S.LeftAnimated>
          )}
          <S.RightAnimated $visible={isVisible(right)} $floating={rightFloating}>
            {right}
          </S.RightAnimated>
        </S.MainAreaContentWrapper>
        <S.BottomAnimated $visible={isVisible(bottom)}>{bottom}</S.BottomAnimated>
      </S.MainArea>
    </S.Container>
  );
};

Page.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired
};
